import {
  CheckIcon,
  ClockIcon,
  CreditCardIcon,
  DocumentDownloadIcon,
  XIcon
} from "@heroicons/react/outline";
import { isEmpty } from "lodash";
import React, { useCallback } from "react";
import { USD_CURRENCY_LOCALE_STRING_CONFIG } from "../../../../components/CurrencySelector";
import { useBillingPlans } from "../../../../context/billingPlans";
import { formatDateToLocale } from "../../../../utils/dates";

const BillingPaymentHistory: React.FC = () => {
  const { invoices, loading } = useBillingPlans();

  const renderStatusIcon = useCallback((status: string) => {
    if (status === "completed")
      return <CheckIcon className="w-6 h-6 dark:text-green-500" />;

    if (status === "pending")
      return <ClockIcon className="w-6 h-6 dark:text-yellow-300" />;

    return <XIcon className="w-6 h-6 dark:text-red-300" />;
  }, []);

  return (
    <>
      <div className="flex items-center mt-7 pb-2 mb-5 border-b border-gray-200 dark:border-gray-500">
        <p className="text-2xl font-semibold">Billing History</p>
      </div>

      <div className="flex rounded-lg bg-white shadow-md dark:bg-gray-800 border dark:border-gray-500 overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b dark:border-gray-500">
              {header.map(({ key, title }) => (
                <th
                  scope="col"
                  key={key}
                  className="px-4 py-4 font-bold text-left text-sm last:px-1 first:pl-6"
                >
                  {title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="relative">
            {isEmpty(invoices) && loading.invoices ? (
              <tr className="w-full py-5 h-28">
                <td
                  style={{ width: "96%" }}
                  className="flex inset-x-0 mx-auto h-5 mt-4 rounded bg-gray-300 dark:bg-gray-600 animate-pulse absolute"
                />
                <td
                  style={{ width: "96%" }}
                  className="flex inset-x-0 mx-auto h-5 mt-12 rounded bg-gray-300 dark:bg-gray-600 animate-pulse absolute"
                />
                <td
                  style={{ width: "96%" }}
                  className="flex inset-x-0 mx-auto h-5 mt-20 rounded bg-gray-300 dark:bg-gray-600 animate-pulse absolute"
                />
              </tr>
            ) : isEmpty(invoices) ? (
              <tr className="w-full flex justify-center items-center py-10">
                <td className="text-sm absolute left-0 right-0 text-center">
                  No results
                </td>
              </tr>
            ) : (
              <>
                {invoices.map(({ number, date, amount, card, last4, pdf }) => (
                  <tr
                    key={number}
                    className="dark:text-gray-200 text-sm border-b last:border-0 dark:border-gray-500"
                  >
                    <td className="py-4 pl-6">
                      {renderStatusIcon("completed")}
                    </td>
                    <td className="px-4 py-4">{number}</td>
                    <td className="px-4 py-4">{formatDateToLocale(date)}</td>
                    <td className="px-4 py-4">
                      <span className="flex items-center">
                        <CreditCardIcon className="w-5 h-5 mr-1.5" />
                        {card} ending {last4}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      {Number(amount / 100).toLocaleString(
                        "en-US",
                        USD_CURRENCY_LOCALE_STRING_CONFIG
                      )}
                    </td>
                    <td className="px-1 py-4">
                      <DocumentDownloadIcon
                        onClick={() => {
                          const a = document.createElement("a");

                          a.target = "_blank";
                          a.href = pdf;
                          a.click();
                        }}
                        className="w-6 h-6 ml-4 text-hotspotty-400 dark:text-hotspotty-300 cursor-pointer dark:hover:opacity-75"
                      />
                    </td>
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default BillingPaymentHistory;

//
// Utils
//

const header = [
  {
    title: "",
    key: "status"
  },
  {
    title: "Number",
    key: "number"
  },
  {
    title: "Date",
    key: "date"
  },
  {
    title: "Payment method",
    key: "payment-method"
  },
  {
    title: "Amount",
    key: "amount"
  },
  {
    title: "Receipt",
    key: "receipt"
  }
];
